import React, { useState, useEffect } from 'react';
import { useIntl, getLocale } from 'umi';
import classnames from 'classnames';
import { Switch, Text } from 'tea-component';
import RangePicker from '@/pages/home/components/RangePicker';
import RangeDayMonthPicker from '@/pages/intelligence/components/RangeDayMonthPicker';
import useRangePicker from './hooks/useRangePicker';
import useRangeMonthPicker from './hooks/useRangeMonthPicker';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { DEFAULT_DATE_TYPES, RANGE_PICKER_DEFAULT_VALUE } from './constants';
import { ComposedDateMode } from './enums';
import { setShortcutOptionFun } from '@/pages/intelligence/components/Filter/FilterBase/_config';
import type { IBaseProps, IDateType, IDateValue } from '@/pages/intelligence/types/common';
import type { IRangePickerDefaultValue } from './types';

import './index.scss';

interface IComposedDatePickerProps extends IBaseProps {
  // 是否禁用
  disabled?: boolean;

  // 选择模式，相对时间、绝对时间
  mode: ComposedDateMode;
  // 时间粒度
  dateType: IDateType;
  // 时间value
  dateValue: IDateValue;
  // 可选时间粒度
  dateTypes?: IDateType[];

  // 绝对时间选择 RangePicker的配置
  rangePickerProps?: {
    defaultValue?: IRangePickerDefaultValue;
    min?: string;
    latestDate?: string;
    disabledDate?: any;
    showShortcut?: boolean;
  };

  // 选择模式change
  onModeChange?: (mode: ComposedDateMode) => void;
  // 整体change，一般是失去焦点 同步最新value给组件调用者
  onChange?: (mode: ComposedDateMode, dateType: IDateType, dateValue: IDateValue) => void;
}

export default function ComposedDatePicker(props: IComposedDatePickerProps) {
  const {
    disabled = false,
    mode: modeOutter = ComposedDateMode.Absolute,
    dateType: dateTypeOutter = 'date',
    dateValue: dateValueOutter = [],
    rangePickerProps,
    onModeChange,
    onChange,
    dateTypes = DEFAULT_DATE_TYPES,
    className,
    style,
  } = props;

  const locale = getLocale();
  const { formatMessage } = useIntl();

  const {
    defaultValue: rangePickerDefaultValue,
    min: rangePickerMin,
    latestDate: rangePickerLatestDate,
    disabledDate: rangePickerDisabledDate,
    showShortcut: rangePickerShowShortcut,
    ...rangePickerRestProps
  } = rangePickerProps || {};

  const mergedRangePickerDefaultValue: IRangePickerDefaultValue = {
    ...RANGE_PICKER_DEFAULT_VALUE,
    ...rangePickerDefaultValue,
  };

  const [mode, setMode] = useState<ComposedDateMode>(modeOutter);
  const handleModeChange = (payload: boolean) => {
    const modeTemp = payload ? ComposedDateMode.Relative : ComposedDateMode.Absolute;
    const dateTypeTemp = modeTemp === ComposedDateMode.Relative ? rangeMonthPickerDateType : rangePickerDateType;
    const dateValueTemp = modeTemp === ComposedDateMode.Relative ? rangeMonthPickerDateValue : rangePickerDateValue;
    setMode(modeTemp);
    onModeChange?.(modeTemp);
    onChange?.(modeTemp, dateTypeTemp, dateValueTemp);
  };
  useEffect(() => {
    setMode(modeOutter);
  }, [modeOutter]);

  const {
    dateType: rangePickerDateType,
    dateValue: rangePickerDateValue,
    pickerDateValue: rangePickerPickerValue,
    onChange: onRangePickerChange,
  } = useRangePicker({
    mode,
    dateType: dateTypeOutter,
    dateValue: dateValueOutter,
    onChange,
  });

  const {
    dateType: rangeMonthPickerDateType,
    dateValue: rangeMonthPickerDateValue,
    onChange: onRangeMonthPickerChange,
  } = useRangeMonthPicker({
    mode,
    dateType: dateTypeOutter,
    dateValue: dateValueOutter,
    onChange,
  });

  return (
    <div style={style} className={classnames('dbi-compare-composed-date-picker', className)}>
      <div className="dbi-compare-composed-date-picker-switch">
        <Switch
          className="dbi-common-switcher"
          disabled={disabled}
          value={mode === ComposedDateMode.Relative}
          onChange={handleModeChange}
        >
          <Text className='dbi-compare-text'>{formatMessage({ id: 'dbi_compare_aligned_by_launch_time' })}</Text>
        </Switch>
      </div>
      {/* 相对时间选择器 */}
      {mode === ComposedDateMode.Relative && (
        <RangeDayMonthPicker
          style={{ marginRight: 10 }}
          disabled={disabled}
          types={dateTypes}
          value={{
            type: rangeMonthPickerDateType,
            value: rangeMonthPickerDateValue,
          }}
          onConfirm={onRangeMonthPickerChange}
        />
      )}
      {/* 绝对时间选择器 */}
      {mode === ComposedDateMode.Absolute && (
        <div className="search-item-date search-item dbi-compare-compose-range-picker-container">
          <RangePicker
            /** 剩余props 放最前面，优先级最低，其他具名的props优先级高于 剩余的props **/
            {...rangePickerRestProps}
            className="search-border"
            disabled={disabled}
            // 需要隐藏的header部分的按钮
            hiddenHeaderBtns={['Latest Data']}
            /** 左侧栏时间粒度 */
            type={rangePickerDateType}
            /** 时间值 */
            value={rangePickerPickerValue}
            setShortcutOption={() => setShortcutOptionFun(rangePickerDateType)}
            /** 日周月年的列表 */
            typeList={dateTypes}
            // startOfWeek={START_OF_WEEK}
            language={locale === LANGUAGE_CN ? 'cn' : 'en'}
            onChange={onRangePickerChange}
            /** 是否有左侧的日周月 */
            showSelectType={dateTypes?.length > 1}
            /** 是否展示弹窗左侧时间筛选 */
            showShortcut={rangePickerShowShortcut}
            latestDate={rangePickerLatestDate}
            disabledDate={rangePickerDisabledDate}
            /** 日周月对应的默认值 */
            defaultValue={mergedRangePickerDefaultValue}
          />
        </div>
      )}
    </div>
  );
}
